import { Copy, Download, MapPin, Search } from "lucide-react";
import React, { useMemo, useState } from "react";
import { Label, TextInput } from "flowbite-react";
import jsPDF from "jspdf";



import { Link, useSearchParams } from "react-router-dom";
import { Heading3, Heading5, NormalParagraph, SmallText, XsmallText } from "../../lib/ui/components/typography/Typography";
import Button from "../../lib/ui/components/button/Button";
import Modal from "../../lib/ui/components/Modal";

const INTERVIEWS_COMPLETED_COUNT = 2;
const INTERVIEWS_TOTAL_COUNT = 5;
const interviewCompletionPercent =
  INTERVIEWS_TOTAL_COUNT > 0
    ? Math.round((INTERVIEWS_COMPLETED_COUNT / INTERVIEWS_TOTAL_COUNT) * 100)
    : 0;
const interviewsRemaining = INTERVIEWS_TOTAL_COUNT - INTERVIEWS_COMPLETED_COUNT;

const MOCK_INTERVIEWS = [
  {
    id: 1,
    role: "Senior Frontend Engineer",
    company: "Acme Corp",
    status: "in progress",
    statusColor: "bg-blue-100 text-blue-800",
    completionDate: "Pending",
    completionDateIso: null,
    nextStep: "Interview is currently underway",
  },
  {
    id: 2,
    role: "Full Stack Developer",
    company: "Globex Inc",
    status: "pending",
    statusColor: "bg-gray-100 text-gray-800",
    completionDate: "Pending",
    completionDateIso: null,
    nextStep: "Candidate has not started this interview",
  },
  {
    id: 3,
    role: "Senior Network Administrator",
    company: "Initech",
    status: "reviewed by employer",
    statusColor: "bg-green-100 text-green-800",
    completionDate: "Mar 28, 2026",
    completionDateIso: "2026-03-28",
    nextStep: "Employer has reviewed this interview",
  },
  {
    id: 4,
    role: "Security Analyst",
    company: "Aperture Labs",
    status: "completed",
    statusColor: "bg-emerald-100 text-emerald-800",
    completionDate: "Mar 20, 2026",
    completionDateIso: "2026-03-20",
    nextStep: "Interview completed successfully",
  },
  {
    id: 5,
    role: "Systems Engineer",
    company: "Soylent Systems",
    status: "abandoned",
    statusColor: "bg-rose-100 text-rose-800",
    completionDate: "Pending",
    completionDateIso: null,
    nextStep: "Interview was abandoned before completion",
  },
  {
    id: 6,
    role: "Platform Engineer",
    company: "Umbrella Tech",
    status: "stopped by applicant",
    statusColor: "bg-orange-100 text-orange-800",
    completionDate: "Pending",
    completionDateIso: null,
    nextStep: "Candidate stopped the interview",
  },
];

/** Mock Q&A exchanges per interview (prototype). Interviewer name matches product copy: June. */
const MOCK_TRANSCRIPT_EXCHANGES = {
  3: [
    {
      question:
        "Walk me through a time you owned reliability for a production service. What did you monitor, and how did you respond to an incident?",
      answer:
        "At my last role I was on-call for our API tier. We used SLOs on latency and error rate with burn-rate alerts. When paging fired, I’d start with a quick health dashboard, then trace a sample of failing requests. In one incident we traced a bad deploy; we rolled back and added a canary gate so it wouldn’t repeat.",
    },
    {
      question: "How do you approach documenting network changes so the team can troubleshoot quickly later?",
      answer:
        "I keep a change log with intent, scope, rollback steps, and validation checks. For bigger changes I add a short runbook section: where to look, common failure modes, and who to escalate to.",
    },
    {
      question: "What’s one security practice you insist on for internal tools or admin access?",
      answer:
        "Least privilege and short-lived credentials. I prefer SSO with MFA, break-glass roles that expire, and audit logs on sensitive actions.",
    },
  ],
  4: [
    {
      question: "Describe how you’d triage a suspected phishing attempt reported by an employee.",
      answer:
        "I’d collect headers and the original message safely, preserve artifacts, check against known campaigns, and block indicators at the mail gateway if needed. Then I’d communicate clearly to the reporter and update awareness training if it’s a new pattern.",
    },
    {
      question: "How do you balance thorough investigation with operational speed during an active alert?",
      answer:
        "I timebox initial enrichment: confirm scope, contain if needed, then parallelize—one thread on eradication, one on evidence. I document decisions as I go so we can improve playbooks afterward.",
    },
  ],
  5: [
    {
      question: "Tell me about a project where requirements shifted mid-flight. How did you adapt?",
      answer:
        "We were rolling out monitoring agents when the vendor changed supported OS versions. I proposed a phased rollout: finish supported hosts first, then a small pilot on the new path with a fallback installer documented for edge cases.",
    },
  ],
  6: [
    {
      question: "What led you to stop the interview, and is there anything we should clarify for next time?",
      answer:
        "I had a schedule conflict I couldn’t move. I’d still like to continue—happy to reschedule when there’s a slot that works.",
    },
  ],
};

const FALLBACK_TRANSCRIPT_EXCHANGES = [
  {
    question: "Thanks for taking the time today. What interested you most about this role?",
    answer:
      "I’m drawn to the team’s focus on reliability and the chance to work on systems at scale. The problems you described map well to what I’ve been doing and what I want next.",
  },
  {
    question: "Is there anything else you’d like us to know?",
    answer: "I appreciate the conversation and I’m happy to share references or a deeper portfolio walkthrough if helpful.",
  },
];

function getTranscriptExchangesForInterview(interviewId) {
  return MOCK_TRANSCRIPT_EXCHANGES[interviewId] ?? FALLBACK_TRANSCRIPT_EXCHANGES;
}

function formatTranscriptPlainText(interview, exchanges) {
  const header = `Interview transcript — ${interview.role} (${interview.company})\n\n`;
  const body = exchanges
    .map(
      (ex) =>
        `June: ${ex.question}\n\nApplicant: ${ex.answer}`
    )
    .join("\n\n---\n\n");
  return header + body;
}

function downloadTranscriptPdf(interview, exchanges) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 16;
  const maxWidth = pageWidth - margin * 2;
  const lineHeight = 6;
  let y = margin;

  const ensureSpace = (needed) => {
    if (y + needed > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(17, 24, 39);
  doc.text(`Interview transcript`, margin, y);
  y += 8;
  doc.setFontSize(11);
  doc.text(interview.role, margin, y);
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(107, 114, 128);
  doc.text(interview.company, margin, y);
  y += 12;
  doc.setTextColor(17, 24, 39);

  for (const ex of exchanges) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    const qLines = doc.splitTextToSize(`June: ${ex.question}`, maxWidth);
    for (const line of qLines) {
      ensureSpace(lineHeight);
      doc.text(line, margin, y);
      y += lineHeight;
    }
    y += 3;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(75, 85, 99);
    const aLines = doc.splitTextToSize(`Applicant: ${ex.answer}`, maxWidth);
    for (const line of aLines) {
      ensureSpace(lineHeight);
      doc.text(line, margin, y);
      y += lineHeight;
    }
    doc.setTextColor(17, 24, 39);
    y += 8;
  }

  const safeSlug = String(interview.company)
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase();
  doc.save(`interview-transcript-${safeSlug || "interview"}-${interview.id}.pdf`);
}

function titleCaseStatus(status) {
  return String(status)
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All statuses" },
  ...[...new Set(MOCK_INTERVIEWS.map((i) => i.status))]
    .sort((a, b) => a.localeCompare(b))
    .map((s) => ({ value: s, label: titleCaseStatus(s) })),
];

function FilterSelect({ id, label, value, onChange, options }) {
  return (
    <div className="flex flex-col gap-1 min-w-[160px] flex-1 sm:max-w-[220px]">
      <Label htmlFor={id} value={label} className="text-body text-gray-500 font-medium" />
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-small text-gray-700 shadow-sm focus:border-[color:var(--primary-400)] focus:ring-1 focus:ring-[color:var(--primary-400)] focus:outline-none transition-colors"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

const MOCK_MATCHING_COMPANIES = [
  {
    id: 1,
    logoVariant: "acme",
    companyName: "Acme Corp",
    role: "Senior Network Administrator",
    completionPercent: 92,
    location: "San Francisco, CA",
  },
  {
    id: 2,
    logoVariant: "globex",
    companyName: "Globex Inc",
    role: "Cloud Infrastructure Engineer",
    completionPercent: 81,
    location: "Austin, TX",
  },
  {
    id: 3,
    logoVariant: "initech",
    companyName: "Initech",
    role: "Product Designer",
    completionPercent: 67,
    location: "New York, NY",
  },
];

function CompanyLogo({ variant, companyName }) {
  if (variant === "acme") {
    return (
      <div
        className="h-10 w-10 rounded-lg flex-shrink-0 flex items-center justify-center shadow-sm"
        style={{ background: "linear-gradient(145deg, #eff6ff 0%, #dbeafe 100%)" }}
        aria-label={`${companyName} logo`}
      >
        <svg viewBox="0 0 40 40" className="h-7 w-7" aria-hidden>
          <path d="M8 29L20 10L32 29H27.8L20 16.5L12.2 29H8Z" fill="#2563eb" />
          <rect x="16.5" y="23" width="7" height="2.8" rx="1.4" fill="#1d4ed8" />
        </svg>
      </div>
    );
  }

  if (variant === "globex") {
    return (
      <div
        className="h-10 w-10 rounded-lg flex-shrink-0 flex items-center justify-center shadow-sm"
        style={{ background: "linear-gradient(145deg, #ecfeff 0%, #cffafe 100%)" }}
        aria-label={`${companyName} logo`}
      >
        <svg viewBox="0 0 40 40" className="h-7 w-7" aria-hidden>
          <circle cx="20" cy="20" r="11" fill="#0891b2" />
          <path d="M13 20C15.6 17.2 18.8 15.8 23.2 15.8C25.4 15.8 27.6 16.2 29.8 17" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" fill="none" />
          <path d="M12.2 23.4C15.2 24.6 18.2 25.1 21.4 25.1C24.3 25.1 27.2 24.6 29.7 23.5" stroke="#67e8f9" strokeWidth="2" strokeLinecap="round" fill="none" />
        </svg>
      </div>
    );
  }

  return (
    <div
      className="h-10 w-10 rounded-lg flex-shrink-0 flex items-center justify-center shadow-sm"
      style={{ background: "linear-gradient(145deg, #f5f3ff 0%, #ede9fe 100%)" }}
      aria-label={`${companyName} logo`}
    >
      <svg viewBox="0 0 40 40" className="h-7 w-7" aria-hidden>
        <rect x="9.5" y="9.5" width="21" height="21" rx="5" fill="#7c3aed" />
        <rect x="13" y="19" width="14" height="2.8" rx="1.4" fill="#ffffff" />
        <rect x="18.6" y="13.2" width="2.8" height="14" rx="1.4" fill="#ffffff" />
      </svg>
    </div>
  );
}

function StatusBadge({ label, colorClass }) {
  const titleCaseLabel = label
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-body font-medium ${colorClass}`}>
      {titleCaseLabel}
    </span>
  );
}

/** Completed, reviewed by employer, abandoned, or stopped — show transcript action instead of complete. */
function isInterviewInTerminalState(status) {
  const s = String(status).toLowerCase();
  return (
    s === "completed" ||
    s === "reviewed by employer" ||
    s === "abandoned" ||
    s.includes("stopped")
  );
}

function getCardAccentClasses(status) {
  const s = String(status).toLowerCase();
  if (s === "in progress" || s === "pending")
    return "border-gray-200 bg-white border-l-[3px] border-l-[color:var(--primary-main)]";
  return "border-gray-200 bg-white";
}

function getInterviewCompletionPercent(status) {
  const s = String(status).toLowerCase();
  if (s === "completed" || s === "reviewed by employer") return 100;
  if (s === "in progress") return 89;
  if (s === "abandoned") return 42;
  if (s.includes("stopped")) return 35;
  return 0;
}

const transcriptActionLinkClass =
  "inline-flex items-center gap-1.5 text-small font-medium bg-transparent border-0 p-0 cursor-pointer hover:underline underline-offset-2";
const transcriptActionPurpleClass = "text-[color:var(--primary-600)] hover:text-[color:var(--primary-700)]";

function InterviewTranscriptModal({ interview, exchanges, show, onClose }) {
  async function handleCopy() {
    const text = formatTranscriptPlainText(interview, exchanges);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard may be unavailable (permissions / non-secure context)
    }
  }

  function handleDownloadPdf() {
    downloadTranscriptPdf(interview, exchanges);
  }

  return (
    <Modal show={show} onClose={onClose} size="2xl" dismissible>
      <Modal.Header className="!border-b border-gray-200">
        <div>
          <span className="block text-h5 font-semibold text-gray-900">Interview transcript</span>
          <span className="mt-1 block text-small text-gray-500">
            {interview.role} · {interview.company}
          </span>
        </div>
      </Modal.Header>
      <Modal.Body className="max-h-[min(70vh,560px)] overflow-y-auto pt-2 sm:pt-4">
        <div className="space-y-4">
          {exchanges.map((ex, index) => (
            <div
              key={index}
              className="rounded-lg border border-gray-200 bg-white p-4 sm:p-5 shadow-sm"
            >
              <p className="text-small font-bold text-gray-900 leading-relaxed">
                June: {ex.question}
              </p>
              <p className="mt-3 text-small font-normal text-gray-600 leading-relaxed">
                Applicant: {ex.answer}
              </p>
            </div>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer className="!border-t border-gray-200 flex flex-wrap items-center justify-end gap-x-5 gap-y-2">
        <button type="button" className={`${transcriptActionLinkClass} ${transcriptActionPurpleClass}`} onClick={handleCopy}>
          <Copy className="h-5 w-5 shrink-0" aria-hidden />
          Copy to Clipboard
        </button>
        <button type="button" className={`${transcriptActionLinkClass} ${transcriptActionPurpleClass}`} onClick={handleDownloadPdf}>
          <Download className="h-5 w-5 shrink-0" aria-hidden />
          Download PDF
        </button>
      </Modal.Footer>
    </Modal>
  );
}

function InterviewCard({ interview, onViewTranscript }) {
  const showsCompletionDate = interview.status === "completed" || interview.status === "reviewed by employer";
  const completionDateText = showsCompletionDate ? interview.completionDate : "Pending";
  const showTranscriptAction = isInterviewInTerminalState(interview.status);
  const accentClasses = getCardAccentClasses(interview.status);
  const completionPercent = getInterviewCompletionPercent(interview.status);
  const progressColor = completionPercent > 80 ? "#16a34a" : "var(--primary-main)";

  return (
    <div className={`border rounded-lg p-5 hover:shadow-sm transition-shadow ${accentClasses}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0">
          <SmallText weight="bold" className="mb-1 text-gray-900">
            {interview.role}
          </SmallText>
          <SmallText className="text-gray-500">{interview.company}</SmallText>
        </div>
        <div className="shrink-0 ml-3">
          <StatusBadge label={interview.status} colorClass={interview.statusColor} />
        </div>
      </div>
      <div className="mt-2">
        <div className="flex items-center justify-between mb-1">
          <XsmallText className="text-gray-500">Interview completion</XsmallText>
          <XsmallText className="text-gray-500 font-semibold">{completionPercent}%</XsmallText>
        </div>
        <div
          className="h-2 w-full rounded-full overflow-hidden"
          style={{ backgroundColor: "var(--primary-100)" }}
          role="progressbar"
          aria-valuenow={completionPercent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Interview completion for ${interview.role} at ${interview.company}`}
        >
          <div
            className="h-full rounded-full transition-[width] duration-300 ease-out"
            style={{ width: `${completionPercent}%`, backgroundColor: progressColor }}
          />
        </div>
      </div>
      <div className="border-t border-gray-100 pt-3 mt-3 flex items-center justify-between gap-3">
        <XsmallText className="text-gray-500 min-w-0 flex-1">
          Interview Completion Date: {completionDateText}
        </XsmallText>
        {showTranscriptAction ? (
          <Button variant="secondary" className="shrink-0" type="button" onClick={() => onViewTranscript(interview.id)}>
            View Transcript
          </Button>
        ) : (
          <Link
            to={`/candidate/dashboard?complete=${interview.id}#recent-interviews`}
            className="no-underline inline-flex shrink-0"
          >
            <Button variant="primary">Complete Interview</Button>
          </Link>
        )}
      </div>
    </div>
  );
}

function MatchingCompanyRow({ company }) {
  const normalizedCompletion = Math.max(0, Math.min(100, company.completionPercent));
  const progressColor = normalizedCompletion > 80 ? "#16a34a" : "var(--primary-main)";

  return (
    <div className="flex items-start gap-3 rounded-lg bg-white p-3 hover:bg-gray-50 transition-colors cursor-pointer">
      <CompanyLogo variant={company.logoVariant} companyName={company.companyName} />
      <div className="flex-1 min-w-0">
        <SmallText weight="bold" className="mb-1 text-gray-900">
          {company.role}
        </SmallText>
        <XsmallText className="text-gray-500">{company.companyName}</XsmallText>
        <div className="flex items-center gap-1 mt-0.5 min-w-0">
          <MapPin className="h-3 w-3 shrink-0 text-gray-400" aria-hidden />
          <XsmallText className="text-gray-400 truncate min-w-0">{company.location}</XsmallText>
        </div>
        <div className="mt-2">
          <div className="flex items-center justify-between mb-1">
            <XsmallText className="text-gray-500">Interview Completion</XsmallText>
            <XsmallText className="text-gray-500 font-semibold">{normalizedCompletion}%</XsmallText>
          </div>
          <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${normalizedCompletion}%`, backgroundColor: progressColor }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CandidateDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const transcriptInterviewId = useMemo(() => {
    const raw = searchParams.get("transcript");
    if (raw == null || raw === "") return null;
    const id = Number(raw);
    if (!Number.isFinite(id)) return null;
    const interview = MOCK_INTERVIEWS.find((i) => i.id === id);
    if (!interview || !isInterviewInTerminalState(interview.status)) return null;
    return id;
  }, [searchParams]);

  const transcriptInterview = transcriptInterviewId
    ? MOCK_INTERVIEWS.find((i) => i.id === transcriptInterviewId)
    : null;
  const transcriptExchanges = transcriptInterview
    ? getTranscriptExchangesForInterview(transcriptInterview.id)
    : [];

  function openTranscript(interviewId) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("transcript", String(interviewId));
        return next;
      },
      { replace: false }
    );
  }

  function closeTranscript() {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("transcript");
        return next;
      },
      { replace: true }
    );
  }

  const filteredInterviews = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return MOCK_INTERVIEWS.filter((interview) => {
      if (q) {
        const inRole = interview.role.toLowerCase().includes(q);
        const inCompany = interview.company.toLowerCase().includes(q);
        if (!inRole && !inCompany) return false;
      }
      if (statusFilter !== "all" && interview.status !== statusFilter) return false;
      return true;
    });
  }, [searchQuery, statusFilter]);

  const hasActiveFilters = Boolean(searchQuery.trim()) || statusFilter !== "all";

  function clearInterviewFilters() {
    setSearchQuery("");
    setStatusFilter("all");
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="mb-8">
        <Heading3 className="mb-2 !text-[color:var(--primary-900)]">Welcome back, Alex</Heading3>
        <NormalParagraph className="text-gray-500">
          Here's what's happening with your interviews.
        </NormalParagraph>
      </div>

      <div className="relative mb-8 overflow-hidden rounded-2xl border border-slate-200/70 bg-[linear-gradient(118deg,#f0f9ff_0%,#f5f3ff_38%,#e0e7ff_70%,rgba(253,242,248,0.42)_100%)] p-4 shadow-sm">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_10%_20%,rgba(255,255,255,0.65),transparent_42%),radial-gradient(circle_at_90%_80%,rgba(255,255,255,0.45),transparent_38%)]" />
        <div className="relative grid grid-cols-3 gap-4">
          <div className="col-span-2 rounded-xl border border-white/70 bg-white/50 p-6 text-center shadow-[0_8px_24px_rgba(136,27,200,0.12)] backdrop-blur-md transition-all hover:-translate-y-0.5 hover:shadow-[0_12px_32px_rgba(136,27,200,0.2)] flex flex-col items-stretch justify-center">
            <div className="mb-2">
              <p className="text-h2 font-bold tabular-nums leading-tight" style={{ color: "var(--primary-700)" }}>
                {INTERVIEWS_COMPLETED_COUNT}
                <span className="text-h4 font-semibold" style={{ color: "var(--primary-600)", opacity: 0.85 }}>
                  {" "}
                  of {INTERVIEWS_TOTAL_COUNT}
                </span>
              </p>
              <SmallText className="text-slate-600 mt-0.5">interviews completed</SmallText>
            </div>
            <div
              className="h-2.5 w-full rounded-full overflow-hidden mb-1.5"
              style={{ backgroundColor: "var(--primary-100)" }}
              role="progressbar"
              aria-valuenow={interviewCompletionPercent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Interview completion progress"
            >
              <div
                className="h-full rounded-full transition-[width] duration-300 ease-out"
                style={{ width: `${interviewCompletionPercent}%`, backgroundColor: "var(--primary-main)" }}
              />
            </div>
            <XsmallText className="text-slate-500 mb-3 block">
              {interviewsRemaining === 0
                ? "You're all caught up."
                : `${interviewsRemaining} left — finish them while you're on a roll.`}
            </XsmallText>
            {interviewsRemaining > 0 && (
              <div className="flex justify-center">
                <Link to="/candidate/dashboard#recent-interviews" className="no-underline">
                  <Button variant="primary">Continue interviews</Button>
                </Link>
              </div>
            )}
          </div>
          <div className="col-span-1 flex flex-col gap-4">
            <div className="rounded-xl border border-white/70 bg-white/55 p-5 text-center shadow-[0_6px_20px_rgba(15,23,42,0.08)] backdrop-blur-md transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_28px_rgba(15,23,42,0.13)] flex-1 flex flex-col justify-center">
              <p className="text-h3 font-bold text-slate-900 mb-1">3</p>
              <SmallText className="text-slate-600">Matching Roles</SmallText>
            </div>
            <div className="rounded-xl border border-white/70 bg-white/50 p-5 text-center shadow-[0_6px_20px_rgba(139,92,246,0.10)] backdrop-blur-md transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_28px_rgba(139,92,246,0.16)] flex-1 flex flex-col justify-center">
              <p className="text-h3 font-bold text-violet-700 mb-1">2</p>
              <SmallText className="text-slate-600">Mostly Pre-Answered Interviews</SmallText>
            </div>
          </div>
        </div>
      </div>

        <div className="grid grid-cols-3 gap-6">
        <div id="recent-interviews" className="col-span-2">
          <Heading5 className="mb-4">Recent Interviews</Heading5>

          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
            <div className="flex-1 min-w-[200px]">
              <Label htmlFor="recentInterviewsSearch" value="Search" className="text-body text-gray-500 font-medium mb-1 block" />
              <TextInput
                id="recentInterviewsSearch"
                type="search"
                icon={Search}
                placeholder="Role or company"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search interviews by role or company"
              />
            </div>
            <FilterSelect
              id="recentInterviewsStatus"
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              options={STATUS_FILTER_OPTIONS}
            />
          </div>

          {hasActiveFilters && (
            <div className="mb-3">
              <button
                type="button"
                onClick={clearInterviewFilters}
                className="text-small text-gray-600 hover:text-gray-900 underline underline-offset-2"
              >
                Clear filters
              </button>
            </div>
          )}

          <div className="space-y-4">
            {filteredInterviews.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/80 px-4 py-8 text-center">
                <SmallText className="text-gray-600">No interviews match your filters.</SmallText>
                {hasActiveFilters && (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={clearInterviewFilters}
                      className="text-small font-medium"
                      style={{ color: "var(--primary-main)" }}
                    >
                      Reset filters
                    </button>
                  </div>
                )}
              </div>
            ) : (
              filteredInterviews.map((interview) => (
                <InterviewCard key={interview.id} interview={interview} onViewTranscript={openTranscript} />
              ))
            )}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <Heading5>Matching Roles</Heading5>
            <Link to="/candidate/companies" className="no-underline shrink-0">
              <SmallText className="cursor-pointer hover:underline" style={{ color: "var(--primary-main)" }}>
                Browse Roles
              </SmallText>
            </Link>
          </div>
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
            {MOCK_MATCHING_COMPANIES.map((company) => (
              <MatchingCompanyRow key={company.id} company={company} />
            ))}
          </div>

          <div className="mt-6">
            <Heading5 className="mb-4">Quick Actions</Heading5>
            <div className="space-y-2">
              <Button variant="secondary" className="w-full">Update Resume</Button>
              <Link to="/candidate/profile?edit=1" className="no-underline block">
                <Button variant="secondary" className="w-full">Edit Profile</Button>
              </Link>
            </div>
          </div>
        </div>
        </div>

      {transcriptInterview ? (
        <InterviewTranscriptModal
          interview={transcriptInterview}
          exchanges={transcriptExchanges}
          show
          onClose={closeTranscript}
        />
      ) : null}
    </div>
  );
}
